import {
    Component, Renderer2, ViewChild, ElementRef, Output, EventEmitter,
    input, signal, InputSignal, WritableSignal    
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ScrollDispatcher, CdkScrollable } from "@angular/cdk/scrolling";
import { AbstractComponent } from "../abstract/abstract.component";
import { AuthService } from "src/app/service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "src/app/service/general.service";
import { User } from "../../entity/User";
import { AccountSelectorComponent } from "./account-selector.component";
import { Company } from "../../entity/Company";
import { firstValueFrom } from "rxjs";
import { UserCompanySelectorDialog } from "./user-company-selector-dialog.component";
import { ClientSelectorDialog } from "./client-selector-dialog.component";
import { Pair } from "../../obj/Pair";

@Component({
    selector: "kilowant-header",
    templateUrl: "../../../html/incs/header.html",
    imports: [AccountSelectorComponent]
})
export class HeaderComponent extends AbstractComponent {
    // Inputs as signals
    title: InputSignal<string> = input<string>('');
    kilowantU: InputSignal<boolean> = input<boolean>(false);
    myKilowant: InputSignal<boolean> = input<boolean>(false);
    showTtitleAsSecond: InputSignal<boolean> = input<boolean>(false);
    isHome: InputSignal<boolean> = input<boolean>(false);
    isIndex: InputSignal<boolean> = input<boolean>(false);
    linkHome: InputSignal<string | null> = input<string | null>(null);
    isSeller: InputSignal<boolean> = input<boolean>(false);
    isBuyer: InputSignal<boolean> = input<boolean>(false);
    showAccountList: InputSignal<boolean> = input<boolean>(true);
    showClose: InputSignal<boolean> = input<boolean>(false);
    hidden: InputSignal<boolean> = input<boolean>(false);
    disabledShadow: InputSignal<boolean> = input<boolean>(false);

    @Output() returned = new EventEmitter<boolean>();

    @ViewChild("headerArea") headerArea: ElementRef;
    @ViewChild("fuelSelector") fuelSelector: ElementRef;

    userCompanySelectorDialogRef: MatDialogRef<UserCompanySelectorDialog> = null!;
    clientSelectorDialogRef: MatDialogRef<ClientSelectorDialog> = null!;

    // State signals
    user: WritableSignal<User | null> = signal<User | null>(null);
    client: WritableSignal<User | null> = signal<User | null>(null);

    companies: (Company|null)[];
    hasMultipleCompanies = signal<boolean>(false);
    userFilter: Pair | null = null;

    showHelp: boolean = false;
    show: boolean = false;
    topShadow: boolean = false;

    constructor(
        router: Router,
        dialog: MatDialog,
        route: ActivatedRoute,
        private authService: AuthService,
        private renderer: Renderer2,
        private scrollDispatcher: ScrollDispatcher,
        analyticsService: AnalyticsService,
        generalService: GeneralService
    ) {
        super(router, route, dialog, analyticsService, generalService);
    }

    ngOnInit(): void {
        if (this.authService.isLoggedIn()) {
            this.generalService.getDetails()
                .then(u => {
                    const user = Object.assign(new User(), u);
                    this.user.set(user);
                    if (this.user()!.isAdvisor) {
                        this.client.set(this.generalService.getStoredClient());
                        if (this.client() == null && this.myKilowant()) {
                            this.clientSelectorOpenPopup();
                        }
                        this.userFilter = this.generalService.getStoredUserFilter();
                        this.refreshView();
                    } else {
                        this.userFilter = this.generalService.getStoredUserFilter();
                        firstValueFrom(this.generalService.getUsersActiveCompanies()).then(companies => {
                            this.companies = companies;
                            if (this.companies.length > 1) {
                                this.hasMultipleCompanies.set(true);
                                if (this.userFilter == null) {
                                    this.generalService.switchStoredUserFilter(this.companies[0] != null, this.companies[0]);
                                }
                            }
                        });
                    }
                    if (this.user() == null) {
                        this.router.navigate(["/user/login"], { queryParams: { redirect: this.router.url } });
                    }
                    return u;
                });
        }
    }

    ngOnDestroy() { }

    ngAfterViewChecked(): void {
        this.generalService.setHeaderHeight(this.headerArea.nativeElement.offsetHeight);
        var t = this;
        if (this.isHome()) {
            this.scrollDispatcher.scrolled()
                .subscribe(event => {
                    if ((<CdkScrollable>event).getElementRef().nativeElement.tagName.toUpperCase() == "MAT-DRAWER-CONTENT") {
                        var x = (<CdkScrollable>event).measureScrollOffset("top");
                        if (this.topShadow != (x != 0)) {
                            if (this.topShadow != (x != 0)) {
                                this.topShadow = (x != 0);
                                if (this.topShadow && !this.disabledShadow()) {
                                    this.renderer["addClass"](this.headerArea.nativeElement, "on");
                                } else {
                                    this.renderer["removeClass"](this.headerArea.nativeElement, "on");
                                }
                            }
                        }
                    }
                });
        }
    }

    switchInfo(): void {
        this.showHelp = !this.showHelp;
    }

    toggleSideNav(): void {
        this.generalService.toggleSidenav();
    }

    open() {
        this.show = true;
    }

    close() {
        this.show = false;
    }

    goToAlerts() {
        this.router.navigate(["user/alerts"]);
    }

    getUserName(): string {
        if (this.userFilter != null && this.userFilter.key) {
            return this.userFilter.value.name;
        } else if (this.userFilter != null) {
            if (this.user()!.isAdvisor) {
                return this.client()!.firstName! + " " + this.client()!.lastName!;
            }
            return this.user()!.firstName! + " " + this.user()!.lastName!;
        }
        if (this.user()!.isAdvisor) {
            return this.client()!.nameAsClient!;
        }
        return this.user()!.nameAsClient!;
    }

    clientSelectorOpenPopup(): void {
        this.clientSelectorDialogRef = this.dialog.open(ClientSelectorDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        this.clientSelectorDialogRef.afterClosed().subscribe(result => {
            this.clientSelectorDialogRef = null!;
            this.userFilter = this.generalService.getStoredUserFilter();
            this.client.set(this.generalService.getStoredClient());
            this.refreshView();
        });
    }

    userCompanySelectorOpenPopup(): void {
        this.userCompanySelectorDialogRef = this.dialog.open(UserCompanySelectorDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        this.userCompanySelectorDialogRef.afterClosed().subscribe(result => {
            this.userCompanySelectorDialogRef = null!;
            if (result) {
                this.userFilter = this.generalService.getStoredUserFilter();
                this.refreshView();
            }
        });
    }

    goto(url: string | null): void {
        if (url == null) {
            return;
        }
        this.generalService.closeSidenav();
        this.router.navigateByUrl(url);
        this.returned.next(true);
    }

    closeClicked(): void {
        this.returned.next(true);
    }

    loginClicked(): void {
        this.returned.next(true);
    }
}